// Analytics: Chat feature events (object_action naming convention)
// These events follow the object_action pattern with `source: "evidence"` for Evidence chat

import type {
  ChipOrigin,
  ChipSurface,
  MessageEntryMethod,
} from '@heidi/evidence/contracts';
import {
  ChatMessageSourceEntry_methodAnyOf0,
  ChatMessageSourceSurfaceAnyOf0,
  type DictationCleanupModes,
} from '@heidi/schema/ml-scribe';

import {
  EVIDENCE_FEATURE,
  type EvidenceBaseEventProps,
  type EvidenceChatAttachmentReason,
  type EvidenceChatBlockType,
  type EvidenceChatErrorType,
  type EvidenceSourceSelectionMode,
} from './evidence';

// Re-export for convenience
export { EVIDENCE_FEATURE };

/**
 * Source identifier for chat events.
 * - 'evidence': Evidence chat (sidebar chat with sources/references)
 * - 'ask_heidi': Legacy Ask Heidi (inline note modification)
 */
export type ChatSource = 'evidence' | 'ask_heidi';

// ─────────────────────────────────────────────────────────────────────────────
// Message Events (object_action pattern)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Base properties for message events with Evidence context.
 */
export interface MessageBaseEventProps extends EvidenceBaseEventProps {
  /** Source of the message event */
  source: ChatSource;
  /** Chat ID */
  chat_id?: string;
  /** Backend message ID (msg_*) */
  message_id?: string;
  /** Session ID if in session */
  session_id?: string;
  /** Whether the active workspace is managed (enterprise/org-managed); undefined until a workspace is active */
  is_managed?: boolean;
}

/**
 * Normalize a raw `queueDurationMs` input into the value we ship as
 * `queue_duration_ms` on `message_submitted` / `message_received` (and on
 * the wire `source` payload).
 *
 * Returns `undefined` when:
 * - the message wasn't actually queued (we don't ship a stale duration with
 *   `queued: false`); OR
 * - the input is missing / not a finite number (catches `NaN` /
 *   `Infinity`, which would otherwise sneak through `typeof === 'number'`
 *   and surface in Amplitude as a literal `NaN`).
 *
 * Otherwise returns the duration clamped to `[0, ∞)` and rounded to ms.
 *
 * Single source of truth so `useEvidenceAnalytics.trackChatSubmitted`,
 * `useEvidenceAnalytics.trackChatCompleted`, and
 * `useChatSendMessage` (wire payload) can never drift apart.
 */
export function normalizeQueueDurationMs(
  queued: boolean | undefined,
  durationMs: number | undefined
): number | undefined {
  if (!queued) return undefined;
  if (typeof durationMs !== 'number' || !Number.isFinite(durationMs)) {
    return undefined;
  }
  return Math.max(0, Math.round(durationMs));
}

/**
 * `entry_method` values the backend `ChatMessageSource` enum accepts.
 * Sourced from generated `ChatMessageSourceEntry_methodAnyOf0` so
 * `pnpm run schema:dev` alone unlocks new wire values. The FE type can move
 * ahead of the backend (e.g. `selection_action`) — sending an unknown value
 * makes the WHOLE chat request fail validation with "Invalid request format",
 * so unknown values must be omitted from the wire.
 */
const WIRE_ENTRY_METHODS = new Set<string>(
  Object.values(ChatMessageSourceEntry_methodAnyOf0)
);

/** `surface` values the backend accepts (`ChatMessageSourceSurfaceAnyOf0`). */
const WIRE_SURFACES = new Set<string>(Object.values(ChatMessageSourceSurfaceAnyOf0));

/** Returns the value if the backend can validate it, else undefined (omit). */
export const toWireEntryMethod = (
  entryMethod: MessageEntryMethod | undefined
): MessageEntryMethod | undefined =>
  entryMethod && WIRE_ENTRY_METHODS.has(entryMethod) ? entryMethod : undefined;

/** Returns the value if the backend can validate it, else undefined (omit). */
export const toWireChipSurface = (
  surface: ChipSurface | undefined
): ChipSurface | undefined =>
  surface && WIRE_SURFACES.has(surface) ? surface : undefined;

/**
 * Wire shape (snake_case) of the analytics-only `source` payload that
 * accompanies a chat completion request. Persisted by the backend on the
 * user message in MongoDB; never used for routing/authorization.
 *
 * Mirrors `ChatSource` (Pydantic) on the backend — keep both in sync.
 */
export interface ChatMessageSourcePayload {
  /** How the user initiated the message (typed vs chip vs banner). */
  entry_method?: MessageEntryMethod;
  /** Display label of the chip/shortcut clicked, if any. */
  chip_label?: string;
  /** Category bucket of the chip, if any. */
  chip_category?: string;
  /** Pin origin: Heidi default, marketplace library, or user custom. */
  origin?: ChipOrigin;
  /** UI surface that produced the click. */
  surface?: ChipSurface;
  /** True when dispatched from the message queue (auto-send / "Send now"). */
  queued?: boolean;
  /** Wait time in the queue before dispatch, ms. Omitted for immediate sends. */
  queue_duration_ms?: number;
}

export interface MessageSubmittedEventProps extends MessageBaseEventProps {
  /** Length of the query in characters */
  query_length: number;
  /** Length of the query in words */
  word_count?: number;
  /** Number of sources selected */
  sources_count?: number;
  /** List of source domains selected */
  sources?: string[];
  /** Selection mode: default, preset, or custom */
  selection_mode: EvidenceSourceSelectionMode;
  /** Preset settings if using preset mode */
  preset_primary_evidence?: boolean;
  preset_local_guidelines?: boolean;
  preset_web_search?: boolean;
  /** Session stage for in-session submissions */
  session_stage?: 'pre' | 'during' | 'post';
  /** How the message was initiated */
  entry_method?: MessageEntryMethod;
  /** Chip display label (shortcut/suggestion only) */
  chip_label?: string;
  /** Chip category (shortcut/suggestion only) */
  chip_category?: string;
  /** Pin origin: Heidi default, marketplace library, or user custom (shortcut chips) */
  origin?: ChipOrigin;
  /** UI surface that produced the click (chip / banner clicks) */
  surface?: ChipSurface;
  /** True when dispatched from the message queue (auto-send / "Send now") */
  queued?: boolean;
  /** Wait time in the queue before dispatch, ms. Omitted for immediate sends. */
  queue_duration_ms?: number;
}

/**
 * Properties for message_received event.
 * Fires when Evidence chat completes a response.
 *
 * Replaces: evidence_chat_completed. Amplitude IS/OOS usage must filter
 * this event with `source: 'evidence'` — the legacy name is a dying remnant.
 */
export interface MessageReceivedEventProps extends MessageBaseEventProps {
  /** State of the message: completed or streaming */
  state: 'completed' | 'streaming';
  /** Total time from submit to completion in milliseconds */
  duration_ms: number;
  /** Time to first token in milliseconds */
  time_to_first_token_ms?: number;
  /** Number of references/sources in the response */
  references_count: number;
  /** Query length in characters */
  query_length: number;
  /** Response length in characters */
  response_length?: number;
  /** Response length in words */
  word_count?: number;
  /** Number of Tavily web searches performed */
  tavily_search_count?: number;
  /** Tools used in this response (e.g., 'evidence_search', 'medical_calculator') */
  tools_used?: string[];
  /** UTM source from sessionStorage, preserved across OAuth redirects */
  custom_utm_source?: string | null;
  /** How the original message was initiated */
  entry_method?: MessageEntryMethod;
  /** Chip display label (shortcut/suggestion only) */
  chip_label?: string;
  /** Chip category (shortcut/suggestion only) */
  chip_category?: string;
  /** Pin origin: Heidi default, marketplace library, or user custom (shortcut chips) */
  origin?: ChipOrigin;
  /** UI surface that produced the click (chip / banner clicks) */
  surface?: ChipSurface;
  /** True when the original message was dispatched from the queue */
  queued?: boolean;
  /** Wait time in the queue before dispatch, ms. Omitted for immediate sends. */
  queue_duration_ms?: number;
}

/**
 * Properties for message_failed event.
 * Fires when Evidence chat encounters an error.
 *
 * Replaces: evidence_chat_error
 */
export interface MessageFailedEventProps extends MessageBaseEventProps {
  /** Error message */
  error_message?: string;
  /** Error code if available */
  error_code?: string;
  /** Time until error occurred in milliseconds */
  duration_ms?: number;
  /** Query length in characters (optional — not all error paths have a query) */
  query_length?: number;
  /** Discriminates which error path fired this event */
  error_type?: EvidenceChatErrorType;
  /** HTTP status code when available */
  http_status?: number;
  /** Block type for block-level errors */
  block_type?: EvidenceChatBlockType;
  /** Reason for attachment errors */
  attachment_reason?: EvidenceChatAttachmentReason;
}

/**
 * Properties for message_aborted event.
 * Fires when user aborts an Evidence chat response.
 *
 * Replaces: evidence_chat_aborted
 */
export interface MessageAbortedEventProps extends MessageBaseEventProps {
  /** Time until user aborted in milliseconds */
  duration_ms?: number;
  /** Query length in characters */
  query_length: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// Chat Lifecycle Events (new events from Notion spec)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Properties for chat_created event.
 * Fires when a new Evidence chat is created (first message sent).
 */
export interface ChatCreatedEventProps extends EvidenceBaseEventProps {
  /** Source of the chat event */
  source: ChatSource;
  /** Chat ID */
  chat_id: string;
  /** Location where chat was created */
  location: string;
  /** Session ID if in session */
  session_id?: string;
}

/**
 * Properties for chat_viewed event.
 * Fires when an existing Evidence chat is viewed/loaded.
 */
export interface ChatViewedEventProps extends EvidenceBaseEventProps {
  /** Source of the chat event */
  source: ChatSource;
  /** Chat ID */
  chat_id: string;
  /** Location where chat was viewed */
  location: string;
  /** Session ID if in session */
  session_id?: string;
}

/**
 * Properties for chat_deleted event.
 * Fires when an Evidence chat is successfully deleted.
 */
export interface ChatDeletedEventProps extends EvidenceBaseEventProps {
  /** Source of the chat event */
  source: ChatSource;
  /** Chat ID that was deleted */
  chat_id: string;
  /** Location where chat was deleted */
  location: string;
  /** Session ID if in session */
  session_id?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Panel Events (object_action pattern)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Panel types that can be opened/closed.
 */
export type PanelType =
  | 'source_control'
  | 'references'
  | 'chat_sidebar'
  | 'task_panel'
  | 'ask_heidi'
  | 'chat_activity_timeline'
  | 'evidence_sidebar'
  | 'entity_verification'
  | 'session_list_panel'
  | 'session_detail_panel'
  // GRO-3284 — post-note quality "Improve your notes" panel + its task cards.
  | 'improve_notes_panel'
  | 'improve_notes_task_card'
  // V2 session page right-side metadata column (the whole panel toggle).
  | 'session_panel'
  // Collapsible cards (sections) inside the V2 session panel:
  // Session, Note context (tab), Codes, Tasks, Tools, Suggested (Verify),
  // and Guidance (in-session surface / visit nudges).
  | 'session_card'
  | 'tab_card'
  | 'codes_card'
  | 'tasks_card'
  | 'tools_card'
  | 'suggested_card'
  | 'guidance_card'
  | 'speaker_popover';

/**
 * Properties for panel_opened and panel_closed events.
 * Replaces: evidence_source_control_toggled, evidence_references_section_toggled
 */
export interface PanelEventProps extends EvidenceBaseEventProps {
  /** Type of panel being opened/closed */
  panel_type: PanelType;
  /** What triggered the action */
  trigger: 'manual' | 'auto' | 'verify_button' | 'deep_link' | 're_analyze';
  /** Feature context (required for evidence panels, optional for others) */
  feature?: typeof EVIDENCE_FEATURE | 'entity_verification';
  /** Additional context for source_control panel */
  active_filters_count?: number;
  /** Additional context for references panel */
  references_count?: number;
  /** Chat ID */
  chat_id?: string;
  /** Session ID */
  session_id?: string;
  /** Mode context (e.g., for Ask Heidi panel) */
  mode?: string;
  /**
   * GRO-3284 — true the first time a user ever sees the improve-notes panel
   * (sourced from the `has_seen_improve_notes_panel` user field), false on
   * subsequent opens. Only set for `improve_notes_panel`.
   */
  first_view?: boolean;
  /**
   * GRO-3284 — which task a card open/close refers to. Only set for
   * `improve_notes_task_card`.
   */
  task_id?: string;
  /**
   * GRO-3284 — for `improve_notes_panel` close: `collapse` (re-openable via the
   * launcher) vs `dismiss` (permanent, GRO-3270).
   */
  close_reason?: 'collapse' | 'dismiss';
}

// ─────────────────────────────────────────────────────────────────────────────
// Toggle Events (object_action pattern)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Boolean-style toggles. Each on/off flip emits one event carrying
 * `is_enabled`.
 */
export type BooleanToggleType =
  | 'evidence_live_suggestions'
  | 'entity_verification_highlight'
  | 'dictation_shortcut_enabled'
  | 'dictation_playback'
  | 'dictation_sound_enabled'
  | 'capture_silence_chime_enabled'
  | 'patient_summary_auto_generation'
  | 'ambient_heidi_enabled';

/**
 * Dropdown/enum-style toggles. Each selection change emits one event
 * carrying `selected_value`. The value type is constrained per toggle so
 * free-form user text can never reach the analytics pipeline.
 */
export type DropdownToggleType = 'dictation_cleanup_mode';

/** Union of every value `toggle_type` is allowed to take. */
export type ToggleType = BooleanToggleType | DropdownToggleType;

/**
 * Shared shape for both variants — feature context + Evidence-specific
 * extras. The discriminated union below pins `toggle_type` to a shape so
 * boolean toggles can never emit `selected_value` and dropdown toggles
 * can never emit `is_enabled`.
 */
interface ToggleUpdatedBase extends EvidenceBaseEventProps {
  /** Feature context */
  feature:
    | typeof EVIDENCE_FEATURE
    | 'entity_verification'
    | 'dictation'
    | 'patient_summary';
  /** Number of enabled categories (for live_suggestions) */
  enabled_categories_count?: number;
  /** Highlight variant (for entity_verification_highlight toggle) */
  highlight_type?: 'verified' | 'issue';
}

interface BooleanToggleUpdatedProps extends ToggleUpdatedBase {
  toggle_type: BooleanToggleType;
  /** Whether the toggle is now enabled. */
  is_enabled: boolean;
  selected_value?: never;
}

interface DropdownToggleUpdatedProps extends ToggleUpdatedBase {
  toggle_type: DropdownToggleType;
  /**
   * Currently-selected value. Constrained per `toggle_type` so the
   * pipeline never sees arbitrary strings. Extend this union when a new
   * dropdown toggle ships.
   */
  selected_value: DictationCleanupModes;
  is_enabled?: never;
}

/**
 * Properties for `toggle_updated`.
 *
 * Replaces: evidence_live_suggestions_toggled.
 *
 * Discriminated on `toggle_type`: a boolean toggle requires `is_enabled`
 * and forbids `selected_value`; a dropdown toggle requires
 * `selected_value` and forbids `is_enabled`. This makes "emit a toggle
 * event without either field" a compile error instead of a JSDoc
 * convention.
 */
export type ToggleUpdatedEventProps =
  | BooleanToggleUpdatedProps
  | DropdownToggleUpdatedProps;

// ─────────────────────────────────────────────────────────────────────────────
// Note Events (object_action pattern)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Update types for note_updated events.
 */
export type NoteUpdateType = 'add' | 'replace';

/**
 * Properties for note_updated event with Evidence source.
 * Fires when Evidence content is added to or replaces a note.
 */
export interface NoteUpdatedFromEvidenceEventProps {
  /** Source of the update */
  source: ChatSource;
  /** Type of update: add or replace */
  update_type: NoteUpdateType;
  /** Feature context */
  feature: typeof EVIDENCE_FEATURE;
  /** Chat ID where content came from */
  chat_id?: string;
  /** Session ID if in session */
  session_id?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Button Action Types for Evidence (used in button_clicked events)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Button actions specific to Evidence feature.
 * Used with button_clicked event and action property.
 */
export type EvidenceButtonAction =
  | 'evidence_suggestion_selected'
  | 'evidence_source_selected'
  | 'attach_session'
  | 'attach_patient'
  | 'upload_file'
  | 'add_to_note'
  | 'replace_note';

// ─────────────────────────────────────────────────────────────────────────────
// Feedback Types for Evidence (used in feedback_submitted events)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Feedback types for Evidence feature.
 */
export type EvidenceFeedbackType = 'response' | 'reference';

/**
 * Properties for feedback_submitted event with Evidence context.
 */
// message_stopped event
export type MessageStoppedEventProps = {
  session_id?: string;
  chat_id?: string;
  /**
   * Backend message ID (msg_*) of the in-flight response the user stopped.
   * Lets message_stopped reconcile 1:1 with the backend evidence.stream.outcome
   * for the same request. Absent if the stream was stopped before the backend
   * emitted message_info.
   */
  message_id?: string;
};

// evidence_clicked event
export type EvidenceClickedAction = 'past_session_clicked' | 'citation_clicked';

export type EvidenceClickedEventProps = {
  session_id?: string;
  chat_id?: string;
  /** Backend message ID (msg_*) the citation belongs to (EVI-1486). */
  message_id?: string;
  click_action: EvidenceClickedAction;
  citation_url?: string;
  citation_title?: string;
  citation_source?: string;
  /**
   * Visual surface that fired the event (EVI-1486).
   * Mirrors `EvidenceSourceSurface` from the unified `evidence_source_clicked`
   * shape; kept loose here to avoid a cross-module type import while we keep
   * the legacy event firing for back-compat.
   */
  surface?: string;
  /** Sub-classification of the interaction within the surface (EVI-1486). */
  interaction?: string;
};

// feature_toggled event
export type FeatureToggledAction = 'chat_mode_toggled' | 'ask_ai_toggle';

export type FeatureToggledEventProps = {
  toggle_action: FeatureToggledAction;
  is_enabled?: boolean;
  session_id?: string;
  previous_mode?: string;
  new_mode?: string;
};

export interface EvidenceFeedbackSubmittedEventProps {
  /** Feature context */
  feature: typeof EVIDENCE_FEATURE;
  /** Type of feedback: response or reference */
  feedback_type: EvidenceFeedbackType;
  /** Rating: positive or negative */
  rating: 'positive' | 'negative';
  /** Whether user provided a comment */
  has_comment: boolean;
  /** User's comment if provided */
  user_comment?: string;
  /** Chat ID */
  chat_id?: string;
  /** Session ID if in session */
  session_id?: string;
  /** Message ID for response feedback */
  message_id?: string;
  /** Reference index for reference feedback */
  reference_index?: number;
  /** Source URL for reference feedback */
  source_url?: string;
  /** Source domain for reference feedback */
  source_domain?: string;
  /** Chat mode */
  mode?: 'legacy' | 'evidence';
}
